import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import {
  addOnlyQuiz,
  updateOnlyQuiz,
  fetchOnlyQuizById,
  clearSelectedOnlyQuiz,
} from "../../../store/reducers/onlyQuizSectionSlice";

import { fetchActiveQuestions } from "../../../store/reducers/questionsSectionSlice";
import DraggableSelectList from "../questionsSection/components/QuestionListDropdown";
import FormPageHeader from "../components/FormPageHeader";

const formPanelStyle = {
  background: "#fff",
  border: "1px solid #eef2f7",
  borderRadius: "10px",
  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
  padding: "18px",
  marginBottom: "18px",
};

const getErrorMessage = (error, fallbackMessage) =>
  error?.error || error?.message || error || fallbackMessage;

const MIN_QUESTIONS_ERROR = "2 questions are required to generate quiz";

const OnlyQuizAddUpdate = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const params = new URLSearchParams(location.search);
  const quizId = params.get("id");

  const { selectedOnlyQuiz, singleLoading } = useSelector(
    (state) => state.onlyQuiz
  );

  const { activeQuestions } = useSelector((state) => state.questions);

  const [options, setOptions] = useState([]);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  const [defaultQuestionIds, setDefaultQuestionIds] = useState([]);

  const [formData, setFormData] = useState({
    name: "",
    name_dk: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    dispatch(fetchActiveQuestions());
  }, [dispatch]);

  useEffect(() => {
    if (activeQuestions) {
      const formatted = activeQuestions.map((q) => ({
        label: `ID# ${q.id} ${q.question}`,
        value: q.id,
      }));
      setOptions(formatted);
    }
  }, [activeQuestions]);

  const handleSelectedIds = (ids) => {
    setSelectedQuestionIds(ids);

    if (ids.length >= 2) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        question_ids: "",
      }));
    }
  };

  useEffect(() => {
    if (quizId) {
      dispatch(fetchOnlyQuizById(quizId));
    }
    return () => dispatch(clearSelectedOnlyQuiz());
  }, [quizId]);

  useEffect(() => {
    if (selectedOnlyQuiz && quizId) {
      setFormData({
        name: selectedOnlyQuiz.name || "",
        name_dk: selectedOnlyQuiz.name_dk || "",
      });

      setSelectedQuestionIds(selectedOnlyQuiz.question_ids || []);
      setDefaultQuestionIds(selectedOnlyQuiz.question_ids || []);
    }
  }, [selectedOnlyQuiz]);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;

    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });

    setErrors({
      ...errors,
      [name]: "",
    });
  };

  const validateForm = () => {
    let newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "English name is required";
    }

    if (!formData.name_dk.trim()) {
      newErrors.name_dk = "Danish name is required";
    }

    if (selectedQuestionIds.length < 2) {
      newErrors.question_ids = MIN_QUESTIONS_ERROR;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      const payload = {
        ...formData,
        question_ids: selectedQuestionIds,
      };

      if (quizId) {
        await dispatch(updateOnlyQuiz({ id: quizId, data: payload })).unwrap();
        toast.success("Quiz Updated Successfully");
      } else {
        await dispatch(addOnlyQuiz(payload)).unwrap();
        toast.success("Quiz Added Successfully");
      }

      history.push("/admin-quiz-section");
    } catch (err) {
      console.error(err);
      toast.error(getErrorMessage(err, "Something went wrong"), {
        position: "top-right",
      });
    }
  };

  return (
    <div className="mt-3">
      <FormPageHeader
        title={quizId ? "Update Quiz" : "Add Quiz"}
        onBack={() => history.push("/admin-quiz-section")}
      />

      {singleLoading ? (
        <div className="text-center p-5">Loading quiz...</div>
      ) : (
        <form onSubmit={handleSubmit} className="p-2">
          <div style={formPanelStyle}>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Name (English)*</label>
                <input
                  type="text"
                  name="name"
                  className={`form-control ${errors.name ? "is-invalid" : ""}`}
                  value={formData.name}
                  onChange={handleChange}
                />
                <div className="invalid-feedback">{errors.name}</div>
              </div>

              <div className="col-md-6">
                <label className="form-label">Name (Danish)*</label>
                <input
                  type="text"
                  name="name_dk"
                  className={`form-control ${errors.name_dk ? "is-invalid" : ""}`}
                  value={formData.name_dk}
                  onChange={handleChange}
                />
                <div className="invalid-feedback">{errors.name_dk}</div>
              </div>
            </div>
          </div>

          <div style={formPanelStyle}>
            <label className="fw-semibold">Select Questions*</label>
            <DraggableSelectList
              options={options}
              defaultValues={defaultQuestionIds}
              onChange={handleSelectedIds}
            />
            {errors.question_ids && (
              <div className="text-danger mt-1">{errors.question_ids}</div>
            )}
          </div>

          <div className="mt-4 d-flex justify-content-end">
            <button type="submit" className="btn bg_color px-4">
              {quizId ? "Update Quiz" : "Add Quiz"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default OnlyQuizAddUpdate;
